/**
 * Outil « Rectangles contre trapèzes » (Boîte à outils, Tle → prépa).
 *
 * L'aire sous la courbe sur [a ; b] approchée par n rectangles (point
 * gauche, droit ou milieu) ou par n trapèzes, à côté de l'intégrale EXACTE
 * donnée par une primitive. Les morceaux sous l'axe comptent négativement
 * (aire signée). En prépa, le tableau des erreurs pour n, 2n, 4n, 8n montre
 * l'ordre de la méthode : erreur divisée par 2 (rectangles gauche/droite) ou
 * par 4 (milieu, trapèzes) quand n double. Calculs : `riemann.ts`.
 */

import { useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn, sampleFunctionPath } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, ParamSlider, Segmented } from '../../ui/controls';
import { makeFrame } from '../../ui/frame';
import { formatNumber, formatSci } from '../../ui/format';
import { FIGURE_BG_CLASS, PlotGrid } from '../../ui/svg-parts';
import {
  approx,
  errorTable,
  exactTolerance,
  fnById,
  FUNCS,
  sameEnds,
  shapes,
  type FnId,
  type Method,
} from './riemann';

const SIZE = 320;
const CURVE = 'var(--color-ink-900)';
const POS = 'var(--color-primary-500)';
const NEG = 'var(--color-error-500)';
const METHODS: ReadonlyArray<{ value: Method; label: string }> = [
  { value: 'left', label: 'À gauche' },
  { value: 'right', label: 'À droite' },
  { value: 'mid', label: 'Milieu' },
  { value: 'trap', label: 'Trapèzes' },
];

/** « π/2 » → « \frac{\pi}{2} », « −1 » → « -1 », « 0,5 » → « 0{,}5 » */
function boundTex(s: string): string {
  return s
    .trim()
    .replace('π/2', '\\frac{\\pi}{2}')
    .replace('2π', '2\\pi')
    .replace('π', '\\pi')
    .replace('−', '-')
    .replace(',', '{,}');
}

const small = (v: number) => (v === 0 ? '0' : v < 0.001 ? formatSci(v, 2) : formatNumber(v, 5));

export function RiemannSumsTool({
  onSend,
  sendCost,
  level,
}: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  const prepa = rank === null || rank >= 8;
  const [id, setId] = useState<FnId>('square');
  const fn = fnById(id);
  const [pi, setPi] = useState(0);
  const p = fn.presets[pi] ?? fn.presets[0]!;
  const [method, setMethod] = useState<Method>('left');
  const [n, setN] = useState(6);

  const sum = approx(fn.f, p.a, p.b, n, method);
  const err = Math.abs(sum - p.value);
  const exact = err < exactTolerance(p);
  const table = prepa ? errorTable(fn, p, method) : [];
  const allExact = table.length > 0 && table.every((r) => r.error === 0);
  const parts = shapes(fn.f, p.a, p.b, n, method);
  const [ba, bb] = p.label.slice(1, -1).split(';');

  // fenêtre : l'intervalle avec une marge, et l'axe des abscisses toujours visible
  const w = p.b - p.a;
  let yMin = 0;
  let yMax = 0;
  for (let i = 0; i <= 100; i++) {
    const y = fn.f(p.a + (w * i) / 100);
    if (Number.isFinite(y)) {
      yMin = Math.min(yMin, y);
      yMax = Math.max(yMax, y);
    }
  }
  const pad = (yMax - yMin) * 0.12 || 1;
  const frame = makeFrame(
    { xMin: p.a - w * 0.12, xMax: p.b + w * 0.12, yMin: yMin - pad, yMax: yMax + pad },
    SIZE,
  );

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Fonction"
        value={id}
        options={FUNCS.map((f) => ({ value: f.id, label: f.label }))}
        onChange={(v) => {
          setId(v);
          setPi(0);
        }}
      />
      <Segmented
        ariaLabel="Intervalle"
        value={String(pi)}
        options={fn.presets.map((q, i) => ({ value: String(i), label: q.label }))}
        onChange={(v) => setPi(Number(v))}
      />
      <Segmented ariaLabel="Méthode" value={method} options={METHODS} onChange={setMethod} />
      <ParamSlider
        name="n (nombre de morceaux)"
        ariaLabel="Nombre de morceaux"
        value={n}
        min={1}
        max={100}
        step={1}
        onChange={setN}
      />

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={`Courbe de ${fn.label} sur ${p.label}, ${n} ${method === 'trap' ? 'trapèzes' : 'rectangles'}.`}
          data-testid="rs-figure"
        >
          <PlotGrid frame={frame} />
          {parts.map((s, i) => {
            const sign = s.y0 + s.y1 >= 0;
            return (
              <polygon
                key={i}
                points={`${frame.tx(s.x0)},${frame.ty(0)} ${frame.tx(s.x0)},${frame.ty(s.y0)} ${frame.tx(s.x1)},${frame.ty(s.y1)} ${frame.tx(s.x1)},${frame.ty(0)}`}
                fill={sign ? POS : NEG}
                fillOpacity={0.22}
                stroke={sign ? POS : NEG}
                strokeWidth={n > 40 ? 0.5 : 1.2}
              />
            );
          })}
          <path
            d={sampleFunctionPath(fn.f, [frame.vp.xMin, frame.vp.xMax], frame)}
            fill="none"
            stroke={CURVE}
            strokeWidth={2.5}
          />
        </svg>
      </div>

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
        data-testid="rs-results"
      >
        <div className="flex flex-wrap items-center gap-2">
          <MathText className="text-ink-900 min-w-0 flex-1 overflow-x-auto text-lg">
            {`$\\int_{${boundTex(ba!)}}^{${boundTex(bb!)}} ${fn.tex}\\,dx = \\Big[${fn.primitiveTex}\\Big]_{${boundTex(ba!)}}^{${boundTex(bb!)}} = ${p.tex}${/^-?\d+$/.test(p.tex) ? '' : ` \\approx ${formatNumber(p.value, 5).replace(',', '{,}')}`}$`}
          </MathText>
          {onSend && (
            <SendToSheetButton
              label={p.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: p.atoms })}
            />
          )}
        </div>
        <p className="text-ink-900 text-sm" aria-live="polite">
          <strong>
            {n} {method === 'trap' ? 'trapèze' : 'rectangle'}
            {n > 1 ? 's' : ''}
          </strong>{' '}
          : somme {exact ? `= ${p.plain}` : `≈ ${formatNumber(sum, 5)}`}, écart avec l’intégrale{' '}
          {exact ? ': 0 (somme exacte)' : `≈ ${small(err)}`}.
        </p>
        {fn.id === 'sin' && p.b > Math.PI && (
          <p className="text-ink-600 text-sm">
            Sous l’axe, f est négative : ces morceaux comptent en négatif (aire signée).
            {exact && ' Ici, les morceaux positifs et négatifs se compensent exactement.'}
          </p>
        )}
      </section>

      {prepa && (
        <section className="space-y-1" aria-label="Erreur selon n" data-testid="rs-errors">
          <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
            Erreur quand n double
          </p>
          {allExact ? (
            <p className="text-ink-700 text-sm">
              {fn.id === 'sin'
                ? 'Erreur nulle pour tout n : sur une période entière, les valeurs de sin se compensent exactement, quelle que soit la méthode.'
                : 'Erreur nulle pour tout n : ici, la méthode donne la valeur exacte.'}
            </p>
          ) : (
            <table className="text-ink-900 w-full text-sm tabular-nums">
              <thead>
                <tr className="text-ink-600">
                  <th className="text-left font-semibold">n</th>
                  <th className="text-right font-semibold">erreur</th>
                  <th className="text-right font-semibold">divisée par</th>
                </tr>
              </thead>
              <tbody>
                {table.map((r) => (
                  <tr key={r.n}>
                    <td>{r.n}</td>
                    <td className="text-right">{small(r.error)}</td>
                    <td className="text-right">
                      {r.ratio === null ? '—' : formatNumber(r.ratio, 2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!allExact && (method === 'left' || method === 'right') && sameEnds(fn, p) && (
            <p className="text-ink-700 text-sm">
              Ici f(a) = f(b) : à gauche, à droite ou en trapèzes, la somme est la même. L’erreur
              est donc en C/n² (divisée par 4), mieux que la règle générale.
            </p>
          )}
          {fn.id === 'sqrt' && p.a === 0 && (method === 'mid' || method === 'trap') && (
            <p className="text-ink-700 text-sm">
              √x n’est pas dérivable en 0 : la régularité manque, l’erreur n’est divisée que par 2,8
              environ (2√2, erreur en C/n<sup>1,5</sup>).
            </p>
          )}
        </section>
      )}

      <Note live={false}>
        {prepa
          ? 'Rectangles à gauche ou à droite : erreur en C/n (divisée par 2 quand n double). Point milieu et trapèzes : erreur en C/n² (divisée par 4), si f est assez régulière.'
          : 'Quand n grandit, les morceaux collent à la courbe et la somme de leurs aires se rapproche de l’intégrale : quand f est positive, c’est l’aire sous la courbe.'}
      </Note>
    </div>
  );
}
